import { useState, useCallback } from 'react';

export const useTaskFilters = () => {
  // Helper to compute current fiscal year range (Apr 1 - Mar 31)
  const getCurrentFiscalYearRange = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const fyStartMonth = 3; // April (0-indexed)
    const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    const first = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
    const last = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
    return {
      startDate: first.toISOString(),
      endDate: last.toISOString(),
      label: 'Current FY'
    };
  };

  // Now allows selecting multiple statuses at once. An empty array means "All".
  const [filterStatus, setFilterStatus] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  // Overdue-only filter (client-side)
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  // Date range filter { startDate, endDate, label }
  const [selectedDateRange, setSelectedDateRange] = useState(() => getCurrentFiscalYearRange());

  // Toggle individual status chips. Passing in "all" clears every status.
  const handleFilterChange = useCallback((status) => {
    console.log('status',status);
    if (status === 'all') {
      setFilterStatus([]);
      return;
    }

    setFilterStatus((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  }, []);

  const handleDepartmentChange = useCallback((departmentId) => {
    setSelectedDepartment(departmentId);
  }, []);

  const handleEmployeeChange = useCallback((employeeId) => {
    setSelectedEmployee(employeeId);
  }, []);

  const handleCustomerChange = useCallback((customerId) => {
    setSelectedCustomer(customerId);
  }, []);

  const handleSupervisorChange = useCallback((supervisorId) => {
    setSelectedSupervisor(supervisorId);
  }, []);

  // Overdue filter handler: enabling overdue clears any date filter
  const handleOverdueFilterChange = useCallback((next) => {
    setShowOverdueOnly(Boolean(next));
    if (next) {
      setSelectedDateRange(null);
    }
  }, []);

  // Date filter handler: supports shortcuts and YYYY-MM-DD strings
  const handleDateFilterChange = useCallback((shortcut) => {
    if (shortcut === 'all') {
      setSelectedDateRange(null);
      return;
    }

    // Selecting a date range/shortcut implies not showing "Overdue only"
    setShowOverdueOnly(false);

    const today = new Date();
    let startDate = null;
    let endDate = null;
    const oneDay = 24 * 60 * 60 * 1000;

    const setRange = (start, end, label) => {
      setSelectedDateRange({ startDate: start.toISOString(), endDate: end.toISOString(), label });
    };

    // Direct range object support { startDate, endDate, label }
    if (shortcut && typeof shortcut === 'object' && shortcut.startDate && shortcut.endDate) {
      setSelectedDateRange(shortcut);
      return;
    }

    switch (shortcut) {
      case 'today': {
        const start = new Date(today.setHours(0, 0, 0, 0));
        const end = new Date(today.setHours(23, 59, 59, 999));
        setRange(start, end, 'Today');
        break;
      }
      case 'this-month': {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        first.setHours(0, 0, 0, 0);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        setRange(first, last, 'This Month');
        break;
      }
      case 'tomorrow': {
        const tomorrow = new Date(Date.now() + oneDay);
        const start = new Date(tomorrow.setHours(0, 0, 0, 0));
        const end = new Date(tomorrow.setHours(23, 59, 59, 999));
        setRange(start, end, 'Tomorrow');
        break;
      }
      case 'this-week': {
        const current = new Date();
        const day = current.getDay(); // 0 (Sun) .. 6 (Sat)
        const diffToMonday = (day === 0 ? -6 : 1 - day); // Monday as first day
        const monday = new Date(current);
        monday.setDate(current.getDate() + diffToMonday);
        monday.setHours(0, 0, 0, 0);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);
        setRange(monday, sunday, 'This Week');
        break;
      }
      case 'next-week': {
        const current = new Date();
        const day = current.getDay();
        const diffToMonday = (day === 0 ? -6 : 1 - day);
        const nextMonday = new Date(current);
        nextMonday.setDate(current.getDate() + diffToMonday + 7);
        nextMonday.setHours(0, 0, 0, 0);
        const nextSunday = new Date(nextMonday);
        nextSunday.setDate(nextMonday.getDate() + 6);
        nextSunday.setHours(23, 59, 59, 999);
        setRange(nextMonday, nextSunday, 'Next Week');
        break;
      }
      default:
        // If shortcut looks like YYYY-MM-DD, set that day as range
        if (typeof shortcut === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(shortcut)) {
          const [y, m, d] = shortcut.split('-').map(Number);
          const start = new Date(y, m - 1, d, 0, 0, 0, 0);
          const end = new Date(y, m - 1, d, 23, 59, 59, 999);
          const label = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
          setRange(start, end, label);
        } else {
          setSelectedDateRange(null);
        }
    }
  }, []);

  const clearFilters = useCallback(() => {
    setFilterStatus([]);
    setSelectedDepartment(null);
    setSelectedEmployee(null);
    setSelectedCustomer(null);
    setSelectedSupervisor(null);
    setShowOverdueOnly(false);
    // Clear date completely (no default week on clear)
    setSelectedDateRange(null);
  }, []);

  const hasActiveFilters = useCallback(() => {
    return filterStatus.length > 0 || selectedDepartment || selectedEmployee || selectedCustomer || selectedSupervisor || selectedDateRange || showOverdueOnly;
  }, [filterStatus, selectedDepartment, selectedEmployee, selectedCustomer, selectedSupervisor, selectedDateRange, showOverdueOnly]);

  const getActiveFilters = useCallback(() => {
    const filters = {};
    if (filterStatus.length > 0) filters.status = filterStatus;
    if (selectedDepartment) filters.departmentId = selectedDepartment;
    if (selectedEmployee) filters.employeeId = selectedEmployee;
    if (selectedCustomer) filters.customerId = selectedCustomer;
    if (selectedSupervisor) filters.supervisorId = selectedSupervisor;
    if (showOverdueOnly) filters.overdue = true;
    if (selectedDateRange) {
      filters.startDate = selectedDateRange.startDate;
      filters.endDate = selectedDateRange.endDate;
    }
    return filters;
  }, [filterStatus, selectedDepartment, selectedEmployee, selectedCustomer, selectedSupervisor, selectedDateRange, showOverdueOnly]);

  const getFilterChips = useCallback((departments = [], employees = [], customers = [], currentUser = null, supervisors = []) => {
    const chips = [];

    if (filterStatus.length > 0) {
      filterStatus.forEach((status) => {
        chips.push({
          type: 'status',
          label: `Status: ${status.replace('-', ' ')}`,
          onRemove: () => handleFilterChange(status)
        });
      });
    }

    if (selectedDepartment) {
      const dept = departments.find(d => d.id === selectedDepartment);
      chips.push({
        type: 'department',
        label: `Department: ${dept?.name || 'Unknown'}`,
        onRemove: () => handleDepartmentChange(null)
      });
    }

    if (selectedEmployee) {
      const emp = employees.find(e => e.id === selectedEmployee);
      chips.push({
        type: 'employee',
        label: `Employee: ${emp?.firstName} ${emp?.lastName}`,
        onRemove: () => handleEmployeeChange(null)
      });
    }

    if (selectedCustomer) {
      const customer = customers.find(c => c.id === selectedCustomer);
      chips.push({
        type: 'customer',
        label: `Customer: ${customer?.name || 'Unknown'}`,
        onRemove: () => handleCustomerChange(null)
      });
    }

    if (selectedSupervisor) {
      const isMyTasks = currentUser && selectedSupervisor === currentUser.id;
      if (isMyTasks) {
        chips.push({
          type: 'supervisor',
          label: 'My Tasks',
          onRemove: () => handleSupervisorChange(null)
        });
      } else {
        const supervisor = supervisors.find(s => s.id === selectedSupervisor) || currentUser;
        chips.push({
          type: 'supervisor',
          label: `Supervisor: ${supervisor?.firstName} ${supervisor?.lastName}`,
          onRemove: () => handleSupervisorChange(null)
        });
      }
    }

    if (showOverdueOnly) {
      chips.push({
        type: 'overdue',
        label: 'Overdue',
        onRemove: () => handleOverdueFilterChange(false)
      });
    }

    if (selectedDateRange) {
      chips.push({
        type: 'date',
        label: `Date: ${selectedDateRange.label}`,
        onRemove: () => handleDateFilterChange('all')
      });
    }

    return chips;
  }, [filterStatus, selectedDepartment, selectedEmployee, selectedCustomer, selectedSupervisor, selectedDateRange, showOverdueOnly, handleFilterChange, handleDepartmentChange, handleEmployeeChange, handleCustomerChange, handleSupervisorChange, handleOverdueFilterChange, handleDateFilterChange]);

  return {
    filterStatus,
    selectedDepartment,
    selectedEmployee,
    selectedCustomer,
    selectedSupervisor,
    showOverdueOnly,
    selectedDateRange,
    handleFilterChange,
    handleDepartmentChange,
    handleEmployeeChange,
    handleCustomerChange,
    handleSupervisorChange,
    handleOverdueFilterChange,
    handleDateFilterChange,
    clearFilters,
    hasActiveFilters,
    getActiveFilters,
    getFilterChips
  };
}; 